import ApiOffers, {defaultParams} from "@/api/methods/ApiOffers";
import {UserDataHelper} from "@/api/helpers";
import {PRIVILEGES} from "@/layout/Constants";
import { clientTz } from "@/plugins/timezone/timezone.helper";

export default {
    namespaced: true,
    state: {
        notes: [],
        selectedOfferType: defaultParams.offerStatus,
        hasContingencyPrivilege: false,
        buyerCompaniesList: [],
        servicerCompaniesList: [],
        brokerCompaniesList: [],
        userViewList: [],
        selectedViewId: null,
        offersCount: {
            active: 0,
            inEscrow: 0,
            completed: 0,
            cancelled: 0,
            expired: 0,
            withdrawn: 0,
            rejected: 0,
            boughtByOther: 0,
            all: 0
        },
        daysForOffersShowing: 30,
        ignoreOfferCreationDate: false,
        addressForSearch: "",
        storeNoteIdForSearch: "",
        sortByField: null,
        sortByDirection: "desc",
    },
    actions: {
        setDefaults({commit}) {
            commit("setNotes", []);
            commit("setSelectedOfferType", defaultParams.offerStatus);
            commit("setBuyerCompanies", []);
            commit("setServicerCompanies", []);
            commit("setBrokerCompanies", []);
            commit("setIgnoreOfferCreationDate", false);
            commit("setAddressForSearch", "");
            commit("setStoreNoteIdForSearch", "");
            commit("setSortByField", null);
            commit("setSortByDirection", "desc");
        },
        async getOffersByTimer({commit, dispatch, state}, params) {
            let user = UserDataHelper.getUser();
            let hasContingencyPrivilege = UserDataHelper.isPrivilegeExists(user, PRIVILEGES.SHOW_CONTINGENCY_IN_OFFER);
            commit("setHasContingencyPrivilege", hasContingencyPrivilege);
            commit("setSelectedOfferType", params.offerStatus);
            params.needPhoto = true;
            params.needStatusScale = true;
            params.timezone = clientTz();
            params.ignoreOfferCreationDate = state.ignoreOfferCreationDate;
            params.address = state.addressForSearch;
            params.storeNoteId = state.storeNoteIdForSearch;
            params.sortByField = state.sortByField;
            params.sortByDirection = state.sortByDirection;
            params.viewId = state.selectedViewId;

            return await ApiOffers.getOffersForBuyer(params).then((response) => {
                let notes = response.data.notes;
                notes.forEach((note) => {
                    note.selectedOffer = note.offersList[0];
                });
                commit("setNotes", notes);
                commit("setBuyerCompanies", response.data.individualCompanyList);
                commit("setServicerCompanies", response.data.servicerCompanyList);
                commit("setBrokerCompanies", response.data.brokerCompanyList);
                commit("setDaysForOffersShowing", response.data.daysForOffersShowing);
                if (response.data.userViewList) {
                    commit("setUserViewList", response.data.userViewList);
                    if (state.selectedViewId === null && response.data.userViewList.length > 0) {
                        const viewList = response.data.userViewList;
                        const hasCustomization = UserDataHelper.isPrivilegeExists(user, PRIVILEGES.SCREEN_CUSTOMIZATION);
                        let selectedView;
                        if (hasCustomization) {
                            selectedView = viewList.find(v => v.isDefault && v.userId == user.userId) || viewList.find(v => v.isDefault && v.userId != user.userId) || viewList.find(v => v.userId === 0);
                        } else {
                            selectedView = viewList.find(v => v.isDefault && v.userId != 0 && v.userId != user.userId) || viewList.find(v => v.userId === 0);
                        }
                        commit("setSelectedViewId", selectedView ? selectedView.userViewId : null);
                    }
                }

                const offersCount = {
                    active: response.data.activeOffersCount,
                    inEscrow: response.data.escrowOffersCount,
                    completed: response.data.completedOffersCount,
                    cancelled: response.data.cancelledOffersCount,
                    expired: response.data.expiredOffersCount,
                    withdrawn: response.data.withdrawnOffersCount,
                    rejected: response.data.rejectedOffersCount,
                    boughtByOther: response.data.boughtByOthersOffersCount,
                    all: response.data.allOffersCount
                };
                commit("setOffersCount", offersCount);
            });
        },
        setServicerCompanies({commit}, companies) {
            commit("setServicerCompanies", companies);
        },
        setIgnoreOfferCreationDate({commit, state}, ignoreOfferCreationDate) {
            commit("setIgnoreOfferCreationDate", ignoreOfferCreationDate);
        },
        setAddressForSearch({commit, state}, addressForSearch) {
            commit("setAddressForSearch", addressForSearch);
        },
        setStoreNoteIdForSearch({commit, state}, storeNoteIdForSearch) {
            commit("setStoreNoteIdForSearch", storeNoteIdForSearch);
        },
        setSortByField({commit, state}, sortByField) {
            commit("setSortByField", sortByField);
        },
        setSortByDirection({commit, state}, sortByDirection) {
            commit("setSortByDirection", sortByDirection);
        },
        setSelectedViewId({commit}, viewId) {
            commit("setSelectedViewId", viewId);
        },
        setUserViewList({commit}, viewList) {
            commit("setUserViewList", viewList);
        }
    },
    mutations: {
        setNotes(state, notes) {
            state.notes = notes;
        },
        setSelectedOfferType(state, offerType) {
            state.selectedOfferType = offerType;
        },
        setHasContingencyPrivilege(state, hasContingencyPrivilege) {
            state.hasContingencyPrivilege = hasContingencyPrivilege;
        },
        setBuyerCompanies(state, companies) {
            state.buyerCompaniesList = companies;
        },
        setServicerCompanies(state, companies) {
            state.servicerCompaniesList = companies;
        },
        setBrokerCompanies(state, companies) {
            state.brokerCompaniesList = companies;
        },
        setOffersCount(state, offersCount) {
            state.offersCount = offersCount;
        },
        setDaysForOffersShowing(state, daysForOffersShowing) {
            state.daysForOffersShowing = daysForOffersShowing;
        },
        setIgnoreOfferCreationDate(state, ignoreOfferCreationDate) {
            state.ignoreOfferCreationDate = ignoreOfferCreationDate;
        },
        setAddressForSearch(state, addressForSearch) {
            state.addressForSearch = addressForSearch;
        },
        setStoreNoteIdForSearch(state, storeNoteIdForSearch) {
            state.storeNoteIdForSearch = storeNoteIdForSearch;
        },
        setSortByField(state, sortByField) {
            state.sortByField = sortByField;
        },
        setSortByDirection(state, sortByDirection) {
            state.sortByDirection = sortByDirection;
        },
        setUserViewList(state, userViewList) {
            state.userViewList = userViewList;
        },
        setSelectedViewId(state, viewId) {
            state.selectedViewId = viewId;
        },
    }
};